import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDownLeft,
  ArrowUpRight,
  BarChart3,
  CircleDollarSign,
  CreditCard,
  Download,
  Phone,
  Star,
  RefreshCw,
  ReceiptText,
  Sparkles,
  UserRound,
  Wallet2,
  X,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import API_URL from "../config/config";
import {
  USER_UPDATED_EVENT,
  clearSessionData,
  formatCurrency,
  getStoredUserName,
  getStoredUserPhone,
  getStoredUserPoints,
  getStoredUserWallet,
  syncSessionUser,
} from "../utils/sessionUser";
import { downloadWalletReceipt } from "../utils/walletReceipt";
import {
  getCachedPageData,
  invalidateCachedPageData,
  setCachedPageData,
} from "../utils/pageDataCache";

const QUICK_TOPUP_AMOUNTS = [100, 200, 500, 1000, 2000];
const getWalletCacheKey = (token) => `wallet:${token || "anonymous"}`;

const loadScript = (src) =>
  new Promise((resolve) => {
    const existingScript = document.querySelector(`script[src="${src}"]`);
    if (existingScript) {
      resolve(true);
      return;
    }

    const script = document.createElement("script");
    script.src = src;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

const parseTimestamp = (value) => {
  if (typeof value === "number") {
    return new Date(value);
  }

  const parsedDate = new Date(value);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
};

const formatDateTime = (value) =>
  new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).format(value);

const sortByNewest = (first, second) =>
  second.timestamp.getTime() - first.timestamp.getTime();

const normalizeCustomerTransaction = (transaction) => {
  const rawOrder = transaction?.order;
  const orderId =
    typeof rawOrder === "string"
      ? rawOrder
      : rawOrder?._id || rawOrder?.toString?.() || "";
  const publicOrderId =
    typeof rawOrder === "object" && rawOrder
      ? rawOrder.orderId || rawOrder?.order_id || ""
      : "";

  return {
    id: transaction?._id || "",
    orderId,
    publicOrderId,
    amount: Number(transaction?.amount) || 0,
    paymentMethodType: transaction?.payment_method_type || "Unknown",
    status: transaction?.status || "completed",
    wallet: Boolean(transaction?.wallet),
    debit: transaction?.debit !== false,
    type: transaction?.type || "payment",
    timestamp: parseTimestamp(transaction?.timestamp || transaction?.createdAt),
    reason: transaction?.reason || "",
    cancellationCharges: Number(transaction?.cancellation_charges) || 0,
    metadata:
      transaction?.metadata && typeof transaction.metadata === "object"
        ? transaction.metadata
        : null,
  };
};

const normalizeTopupTransaction = (transaction) => ({
  id: transaction?._id || "",
  amount: Number(transaction?.amount) || 0,
  razorpayPaymentId: transaction?.razorpayPaymentId || "",
  type: transaction?.type || "topup",
  timestamp: parseTimestamp(transaction?.timestamp || transaction?.createdAt),
});

const fetchOrderReferences = async (orderIds) => {
  const uniqueOrderIds = [...new Set(orderIds.filter(Boolean))];
  if (!uniqueOrderIds.length) {
    return {};
  }

  const responses = await Promise.allSettled(
    uniqueOrderIds.map(async (orderId) => {
      const response = await axios.get(`${API_URL}/order/find`, {
        params: { orderId },
      });

      const order = response.data?.order;
      return [
        orderId,
        {
          publicOrderId: order?.orderId || orderId,
          status: order?.status || "",
          paymentMethod: order?.payment_method || "",
        },
      ];
    })
  );

  return responses.reduce((lookup, response) => {
    if (response.status === "fulfilled") {
      const [orderId, details] = response.value;
      lookup[orderId] = details;
    }
    return lookup;
  }, {});
};

const getPaymentMethodLabel = (transaction) => {
  if (transaction.kind === "topup") {
    return "Razorpay Wallet Top-up";
  }

  if (transaction.paymentMethodType === "points") {
    return "Reward Points";
  }

  if (transaction.paymentMethodType === "points_redemption") {
    return "Points Redemption";
  }

  if (transaction.wallet) {
    return "Wallet";
  }

  return transaction.paymentMethodType || "Online";
};

const getTransactionMeta = (transaction) => {
  const absoluteAmount = Math.abs(Number(transaction?.amount) || 0);

  if (transaction.kind === "topup") {
    return {
      title: "Wallet Top-up",
      subtitle: "Money added to your wallet through Razorpay",
      amountText: `+${formatCurrency(transaction.amount)}`,
      amountClass: "text-emerald-600",
      iconWrapClass:
        "bg-emerald-100 text-emerald-700 shadow-[0_12px_35px_rgba(16,185,129,0.18)]",
      chipClass: "border border-emerald-200 bg-emerald-50 text-emerald-700",
      chipLabel: "Top up",
      cardBorderClass: "border-emerald-100",
      icon: ArrowDownLeft,
    };
  }

  const lowerReason = String(transaction.reason || "").toLowerCase();
  const isPointsCredit =
    transaction.paymentMethodType === "points" && transaction.debit === false;
  const isPointsRedeem = transaction.paymentMethodType === "points_redemption";
  const isRefund = transaction.type === "refund";
  const isCancellation = transaction.type === "cancellation";
  const isWalletDue = transaction.type === "wallet_due_payment";
  const isWalletDebit = transaction.wallet && transaction.debit;

  if (isPointsCredit) {
    return {
      title: lowerReason.includes("cancellation")
        ? "Cancellation Reward"
        : "Points Added",
      subtitle: transaction.reason || "Reward points credited to your account",
      amountText: `+${Math.round(transaction.amount)} pts`,
      amountClass: "text-sky-600",
      iconWrapClass:
        "bg-sky-100 text-sky-700 shadow-[0_12px_35px_rgba(14,165,233,0.18)]",
      chipClass: "border border-sky-200 bg-sky-50 text-sky-700",
      chipLabel: "Points",
      cardBorderClass: "border-sky-100",
      icon: Sparkles,
    };
  }

  if (isPointsRedeem) {
    return {
      title: "Points Redeemed",
      subtitle: "Reward points used against the order amount",
      amountText: `-${Math.round(absoluteAmount)} pts`,
      amountClass: "text-violet-600",
      iconWrapClass:
        "bg-violet-100 text-violet-700 shadow-[0_12px_35px_rgba(139,92,246,0.18)]",
      chipClass: "border border-violet-200 bg-violet-50 text-violet-700",
      chipLabel: "Points",
      cardBorderClass: "border-violet-100",
      icon: Sparkles,
    };
  }

  if (isRefund) {
    return {
      title: "Wallet Refund",
      subtitle: transaction.reason || "Refund credited back to your wallet",
      amountText: `+${formatCurrency(transaction.amount)}`,
      amountClass: "text-emerald-600",
      iconWrapClass:
        "bg-emerald-100 text-emerald-700 shadow-[0_12px_35px_rgba(16,185,129,0.18)]",
      chipClass: "border border-emerald-200 bg-emerald-50 text-emerald-700",
      chipLabel: "Credit",
      cardBorderClass: "border-emerald-100",
      icon: ArrowDownLeft,
    };
  }

  if (isCancellation) {
    return {
      title: "Cancellation Charge",
      subtitle:
        transaction.reason || "Amount adjusted because the order was cancelled",
      amountText: `-${formatCurrency(absoluteAmount)}`,
      amountClass: "text-rose-600",
      iconWrapClass:
        "bg-rose-100 text-rose-700 shadow-[0_12px_35px_rgba(244,63,94,0.16)]",
      chipClass: "border border-rose-200 bg-rose-50 text-rose-700",
      chipLabel: "Debit",
      cardBorderClass: "border-rose-100",
      icon: ArrowUpRight,
    };
  }

  if (isWalletDue) {
    return {
      title: "Wallet Due Settled",
      subtitle: transaction.reason || "Previous wallet due was cleared",
      amountText: formatCurrency(transaction.amount),
      amountClass: "text-amber-600",
      iconWrapClass:
        "bg-amber-100 text-amber-700 shadow-[0_12px_35px_rgba(245,158,11,0.18)]",
      chipClass: "border border-amber-200 bg-amber-50 text-amber-700",
      chipLabel: "Due",
      cardBorderClass: "border-amber-100",
      icon: CircleDollarSign,
    };
  }

  if (isWalletDebit) {
    return {
      title: "Wallet Order Payment",
      subtitle: "Order amount paid directly from your wallet balance",
      amountText: `-${formatCurrency(absoluteAmount)}`,
      amountClass: "text-orange-600",
      iconWrapClass:
        "bg-orange-100 text-orange-700 shadow-[0_12px_35px_rgba(249,115,22,0.18)]",
      chipClass: "border border-orange-200 bg-orange-50 text-orange-700",
      chipLabel: "Wallet pay",
      cardBorderClass: "border-orange-100",
      icon: Wallet2,
    };
  }

  return {
    title: "Online Order Payment",
    subtitle: "Transaction recorded for an online order payment",
    amountText: `-${formatCurrency(absoluteAmount)}`,
    amountClass: "text-slate-700",
    iconWrapClass:
      "bg-slate-100 text-slate-700 shadow-[0_12px_35px_rgba(100,116,139,0.16)]",
    chipClass: "border border-slate-200 bg-slate-50 text-slate-700",
    chipLabel: "Online",
    cardBorderClass: "border-slate-100",
    icon: ReceiptText,
  };
};

const getReceiptFields = ({ record, orderSummary, userProfile }) => {
  const meta = getTransactionMeta(record);
  const displayOrderId =
    orderSummary?.publicOrderId || record.displayOrderId || record.orderId || "-";

  const fields = [
    { label: "Receipt ID", value: record.id || "-" },
    { label: "Transaction", value: meta.title },
    { label: "Amount", value: meta.amountText.replace("+", "") },
    { label: "Date", value: formatDateTime(record.timestamp) },
    { label: "Payment Method", value: getPaymentMethodLabel(record) },
    { label: "Status", value: record.status || "completed" },
  ];

  if (record.kind === "transaction" && displayOrderId !== "-") {
    fields.push({ label: "Order ID", value: displayOrderId });
  }

  if (record.kind === "topup" && record.razorpayPaymentId) {
    fields.push({
      label: "Razorpay Payment ID",
      value: record.razorpayPaymentId,
    });
  }

  if (record.kind === "transaction" && record.reason) {
    fields.push({ label: "Reason", value: record.reason });
  }

  fields.push(
    { label: "Customer", value: userProfile.fullname || "User" },
    { label: "Phone", value: userProfile.mobileno || "-" }
  );

  return fields;
};


const HistoryCard = ({ record, orderSummary, onOpen }) => {
  const meta = getTransactionMeta(record);
  const Icon = meta.icon;
  const displayOrderId =
    orderSummary?.publicOrderId || record.displayOrderId || record.publicOrderId;

  return (
    <button
      type="button"
      onClick={() => onOpen(record)}
      className={`group w-full rounded-[22px] border ${meta.cardBorderClass} bg-white/92 p-4 text-left shadow-[0_18px_50px_rgba(15,23,42,0.06)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(15,23,42,0.12)] sm:rounded-[26px] sm:p-5`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-3 sm:gap-4">
          <div
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${meta.iconWrapClass} sm:h-14 sm:w-14 sm:rounded-2xl`}
          >
            <Icon size={20} />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-base font-semibold text-slate-900 sm:text-lg">
                {meta.title}
              </h3>
              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${meta.chipClass} sm:px-3 sm:text-[11px] sm:tracking-[0.18em]`}
              >
                {meta.chipLabel}
              </span>
            </div>
            <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm sm:leading-6">
              {meta.subtitle}
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs sm:text-sm">
              <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600 sm:px-3">
                {getPaymentMethodLabel(record)}
              </span>
              {displayOrderId ? (
                <span className="rounded-full bg-amber-50 px-2.5 py-1 font-semibold text-amber-700 sm:px-3">
                  {displayOrderId}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="text-left lg:text-right">
          <div className={`text-lg font-bold ${meta.amountClass} sm:text-xl`}>
            {meta.amountText}
          </div>
          <div className="mt-1 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400 sm:text-xs sm:tracking-[0.18em]">
            {formatDateTime(record.timestamp)}
          </div>
          <div className="mt-2 text-xs font-semibold text-orange-500 transition group-hover:text-orange-600 sm:mt-3 sm:text-sm">
            View Details
          </div>
        </div>
      </div>
    </button>
  );
};

const TransactionDetailModal = ({
  record,
  orderSummary,
  userProfile,
  onClose,
  onDownload,
}) => {
  if (!record) {
    return null;
  }

  const meta = getTransactionMeta(record);
  const Icon = meta.icon;
  const detailRows = getReceiptFields({ record, orderSummary, userProfile });
  const displayOrderId =
    orderSummary?.publicOrderId || record.displayOrderId || record.publicOrderId;

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-slate-950/55 p-2 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[24px] border border-white/70 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.24)] sm:rounded-[28px]">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-4 sm:gap-4 sm:px-7 sm:py-5">
          <div className="flex min-w-0 items-start gap-3 sm:gap-4">
            <div
              className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${meta.iconWrapClass} sm:h-14 sm:w-14 sm:rounded-2xl`}
            >
              <Icon size={20} />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-950 sm:text-2xl">
                {meta.title}
              </h2>
              <p className="mt-1 text-xs leading-5 text-slate-500 sm:text-sm sm:leading-6">
                {meta.subtitle}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition hover:bg-slate-200 sm:h-10 sm:w-10"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-4 sm:px-7 sm:py-6">
          <div className="rounded-[18px] border border-slate-200 bg-slate-50 px-4 py-4 sm:rounded-[24px] sm:px-5 sm:py-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400 sm:text-xs sm:tracking-[0.22em]">
                  Amount
                </p>
                <p className={`mt-2 text-3xl font-black ${meta.amountClass} sm:text-4xl`}>
                  {meta.amountText}
                </p>
                <p className="mt-2 text-xs font-medium text-slate-500 sm:text-sm">
                  {formatDateTime(record.timestamp)}
                </p>
              </div>

              <div className="flex flex-wrap gap-2 sm:justify-end">
                <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 shadow-sm sm:px-3 sm:text-sm">
                  {getPaymentMethodLabel(record)}
                </span>
                {displayOrderId ? (
                  <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-amber-700 shadow-sm sm:px-3 sm:text-sm">
                    {displayOrderId}
                  </span>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-4 overflow-hidden rounded-[18px] border border-slate-200 sm:mt-6 sm:rounded-[22px]">
            {detailRows.map((detail, index) => (
              <div
                key={`${detail.label}-${detail.value}`}
                className={`flex flex-col gap-1.5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4 ${
                  index !== detailRows.length - 1 ? "border-b border-slate-100" : ""
                }`}
              >
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 sm:text-xs sm:tracking-[0.2em]">
                  {detail.label}
                </p>
                <p className="max-w-[28rem] break-words text-xs font-semibold text-slate-900 sm:text-right sm:text-sm">
                  {detail.value}
                </p>
              </div>
            ))}
          </div>

          {(orderSummary?.status || orderSummary?.paymentMethod) && (
            <div className="mt-4 flex flex-wrap gap-2 sm:mt-5">
              {orderSummary?.status ? (
                <span className="rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 sm:px-3 sm:text-sm">
                  Status: {orderSummary.status}
                </span>
              ) : null}
              {orderSummary?.paymentMethod ? (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 sm:px-3 sm:text-sm">
                  {orderSummary.paymentMethod}
                </span>
              ) : null}
            </div>
          )}

          <div className="mt-5 flex justify-stretch sm:mt-6 sm:justify-end">
            <button
              type="button"
              onClick={onDownload}
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 sm:w-auto"
            >
              <Download size={16} />
              Download Bill
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Wallet = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const cachedWallet = getCachedPageData(
    getWalletCacheKey(localStorage.getItem("token"))
  );
  const [walletBalance, setWalletBalance] = useState(
    () => Number(cachedWallet?.user?.wallet) || getStoredUserWallet()
  );
  const [userProfile, setUserProfile] = useState({
    fullname: cachedWallet?.user?.fullname || getStoredUserName(),
    mobileno: cachedWallet?.user?.mobileno || getStoredUserPhone(),
    wallet: Number(cachedWallet?.user?.wallet) || getStoredUserWallet(),
    points: Number(cachedWallet?.user?.points) || getStoredUserPoints(),
  });
  const [activeTab, setActiveTab] = useState(
    location.state?.initialTab === "topup" ? "topup" : "transactions"
  );
  const [transactions, setTransactions] = useState(
    () => cachedWallet?.transactions || []
  );
  const [topups, setTopups] = useState(() => cachedWallet?.topups || []);
  const [orderLookup, setOrderLookup] = useState(
    () => cachedWallet?.orderLookup || {}
  );
  const [loading, setLoading] = useState(() => !cachedWallet);
  const [refreshing, setRefreshing] = useState(false);
  const [processingTopUp, setProcessingTopUp] = useState(false);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const walletRequestRef = useRef(0);
  const topUpSectionRef = useRef(null);

  const openTopUpSection = () => {
    setActiveTab("topup");
    window.requestAnimationFrame(() => {
      topUpSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const applyWalletSnapshot = (snapshot, { cache = true } = {}) => {
    setUserProfile(snapshot.user);
    setWalletBalance(Number(snapshot.user?.wallet) || 0);
    setTransactions(snapshot.transactions);
    setTopups(snapshot.topups);
    setOrderLookup(snapshot.orderLookup || {});
    if (cache) {
      setCachedPageData(getWalletCacheKey(localStorage.getItem("token")), snapshot);
    }
  };

  const fetchWalletData = async ({ silent = false, force = false } = {}) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.info("Please sign in to access your wallet.");
      navigate("/", { replace: true });
      return;
    }

    const cacheKey = getWalletCacheKey(token);
    const cachedSnapshot = !force ? getCachedPageData(cacheKey) : null;
    if (cachedSnapshot) {
      applyWalletSnapshot(cachedSnapshot, { cache: false });
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (!silent) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    try {
      const requestId = ++walletRequestRef.current;
      const headers = { Authorization: `Bearer ${token}` };
      const [user, transactionsResponse, topupsResponse] = await Promise.all([
        syncSessionUser(token),
        axios.get(`${API_URL}/customer-transactions/get3`, { headers }),
        axios.get(`${API_URL}/customer-transactions/get2`, { headers }),
      ]);

      const rawTransactions = (transactionsResponse.data?.transactions || [])
        .map(normalizeCustomerTransaction)
        .sort(sortByNewest);
      const nextTransactions = rawTransactions.map((transaction) => ({
        ...transaction,
        kind: "transaction",
        displayOrderId: transaction.publicOrderId || "",
      }));
      const nextTopups = (topupsResponse.data?.transactions || [])
        .map(normalizeTopupTransaction)
        .sort(sortByNewest)
        .map((topup) => ({ ...topup, kind: "topup", status: "completed" }));

      applyWalletSnapshot({
        user,
        transactions: nextTransactions,
        topups: nextTopups,
        orderLookup: {},
      });

      // Order references are only supplementary labels in the transaction
      // history. Load them after the wallet balance and history are visible,
      // rather than making the initial page render wait for one request per
      // transaction.
      void fetchOrderReferences(
        rawTransactions.map((transaction) => transaction.orderId)
      ).then((references) => {
        if (walletRequestRef.current !== requestId) {
          return;
        }

        setOrderLookup(references);
        setTransactions((currentTransactions) =>
          currentTransactions.map((transaction) => ({
            ...transaction,
            displayOrderId:
              transaction.publicOrderId ||
              references[transaction.orderId]?.publicOrderId ||
            "",
          }))
        );

        const currentSnapshot = getCachedPageData(cacheKey);
        if (currentSnapshot) {
          setCachedPageData(cacheKey, {
            ...currentSnapshot,
            orderLookup: references,
            transactions: currentSnapshot.transactions.map((transaction) => ({
              ...transaction,
              displayOrderId:
                transaction.publicOrderId ||
                references[transaction.orderId]?.publicOrderId ||
                "",
            })),
          });
        }
      });
    } catch (error) {
      console.error("Wallet fetch error:", error);
      if (error?.status === 401 || error?.response?.status === 401) {
        clearSessionData();
        toast.error("Session expired. Please sign in again.");
        navigate("/", { replace: true });
      } else {
        toast.error(
          error?.response?.data?.message ||
            error?.message ||
            "Unable to load wallet details"
        );
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (location.state?.initialTab === "topup") {
      setActiveTab("topup");
    }
  }, [location.state]);

  useEffect(() => {
    const syncViewState = (event) => {
      invalidateCachedPageData(
        getWalletCacheKey(localStorage.getItem("token"))
      );
      const eventUser = event?.detail;
      if (eventUser && typeof eventUser === "object") {
        setUserProfile((currentUser) => ({
          ...currentUser,
          ...eventUser,
          points:
            typeof eventUser.points !== "undefined"
              ? Number(eventUser.points) || 0
              : currentUser.points,
        }));
        setWalletBalance(Number(eventUser.wallet) || 0);
        return;
      }

      setUserProfile({
        fullname: getStoredUserName(),
        mobileno: getStoredUserPhone(),
        wallet: getStoredUserWallet(),
        points: getStoredUserPoints(),
      });
      setWalletBalance(getStoredUserWallet());
    };

    window.addEventListener(USER_UPDATED_EVENT, syncViewState);
    void fetchWalletData();

    return () => {
      window.removeEventListener(USER_UPDATED_EVENT, syncViewState);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleTopUp = async () => {
    const amount = Number(topUpAmount);
    if (!Number.isInteger(amount)) {
      toast.error("Please enter whole numbers only.");
      return;
    }

    if (amount < 10) {
      toast.error("Minimum wallet top-up is ₹10.");
      return;
    }

    const token = localStorage.getItem("token");
    if (!token) {
      toast.info("Please sign in to add money to your wallet.");
      navigate("/", { replace: true });
      return;
    }

    setProcessingTopUp(true);

    try {
      const sdkLoaded = await loadScript(
        "https://checkout.razorpay.com/v1/checkout.js"
      );
      if (!sdkLoaded) {
        toast.error("Failed to load Razorpay. Please try again.");
        setProcessingTopUp(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const orderResponse = await axios.post(
        `${API_URL}/razorpay/create-order`,
        {
          amount,
          currency: "INR",
          receipt: `wallet_topup_${Date.now()}`,
        },
        { headers }
      );

      const contactNumber = String(userProfile?.mobileno || "")
        .replace(/\D/g, "")
        .slice(-10);

      const options = {
        key: process.env.REACT_APP_PAY,
        amount: amount * 100,
        currency: "INR",
        name: "Instaport Wallet",
        description: "Wallet Top-up",
        image: "https://instaportdelivery.com/assets/logo/logo.png",
        order_id: orderResponse.data.id,
        prefill: {
          name: userProfile?.fullname || "Instaport Customer",
          contact: contactNumber,
        },
        theme: {
          color: "#F59E0B",
        },
        modal: {
          ondismiss: () => {
            setProcessingTopUp(false);
          },
        },
        handler: async (response) => {
          try {
            await axios.post(
              `${API_URL}/customer-transactions/topup`,
              {
                amount,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers }
            );

            toast.success(`₹${amount} added to your wallet successfully.`);
            setTopUpAmount("");
            setActiveTab("topup");
            await fetchWalletData({ silent: true, force: true });
          } catch (error) {
            console.error("Wallet top-up error:", error);
            toast.error(
              error?.response?.data?.message || "Failed to confirm wallet top-up"
            );
          } finally {
            setProcessingTopUp(false);
          }
        },
      };

      const razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on("payment.failed", () => {
        toast.error("Payment was not completed.");
        setProcessingTopUp(false);
      });
      razorpayInstance.open();
    } catch (error) {
      console.error("Top-up initialization error:", error);
      toast.error(
        error?.response?.data?.message ||
          "Unable to start wallet top-up right now"
      );
      setProcessingTopUp(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!selectedRecord) {
      return;
    }

    const selectedOrderSummary = selectedRecord.orderId
      ? orderLookup[selectedRecord.orderId] || {
          publicOrderId: selectedRecord.displayOrderId || selectedRecord.orderId,
        }
      : null;
    const meta = getTransactionMeta(selectedRecord);

    try {
      await downloadWalletReceipt({
        title: meta.title,
        subtitle: `${getPaymentMethodLabel(selectedRecord)} • ${formatDateTime(
          selectedRecord.timestamp
        )}`,
        amountText: meta.amountText,
        fields: getReceiptFields({
          record: selectedRecord,
          orderSummary: selectedOrderSummary,
          userProfile,
        }),
        fileName: `instaport-bill-${
          selectedOrderSummary?.publicOrderId || selectedRecord.id || Date.now()
        }.png`,
      });

      toast.success("Bill downloaded successfully.");
    } catch (error) {
      console.error("Receipt download error:", error);
      toast.error("Unable to generate bill right now.");
    }
  };

  const totalActivities = transactions.length + topups.length;
  const completedTopupValue = topups.reduce(
    (sum, topup) => sum + Number(topup.amount || 0),
    0
  );
  const pointsBalance = Number(userProfile?.points || 0);
  const selectedOrderSummary = selectedRecord?.orderId
    ? orderLookup[selectedRecord.orderId] || {
        publicOrderId:
          selectedRecord.displayOrderId ||
          selectedRecord.publicOrderId ||
          selectedRecord.orderId,
      }
    : null;
  const heroStats = [
    {
      key: "activity",
      label: "Total Activity",
      value: totalActivities,
      description: "All-time wallet activity",
      icon: BarChart3,
      iconWrapClass: "bg-[#eaf8ee] text-[#138536]",
    },
    {
      key: "topup",
      label: "Top-up Value",
      value: formatCurrency(completedTopupValue),
      description: "Total amount added",
      icon: Wallet2,
      iconWrapClass: "bg-[#fff4d9] text-[#e39b00]",
    },
    {
      key: "points",
      label: "Points",
      value: `${Math.round(pointsBalance)} pts`,
      description: "Available reward points",
      icon: Star,
      iconWrapClass: "bg-[#eaf8ee] text-[#17933c]",
    },
  ];

  return (
    <section className="wallet-shell min-h-screen w-full overflow-hidden font-Poppins">
      <div className="wallet-orb absolute left-[-8rem] top-24 hidden h-72 w-72 rounded-full bg-[#ffd54b]/35 blur-3xl sm:block" />
      <div className="wallet-orb absolute bottom-20 right-[-6rem] hidden h-80 w-80 rounded-full bg-[#0f8b8d]/15 blur-3xl [animation-delay:1.5s] sm:block" />

      <Navbar />

      <div className="relative z-10 w-full px-3 pb-14 pt-[96px] sm:px-4 sm:pb-20 sm:pt-[110px] lg:px-6">
        <div className="wallet-panel wallet-pattern overflow-hidden rounded-[28px] border border-white/70 px-3 py-3 shadow-[0_22px_80px_rgba(15,23,42,0.10)] sm:rounded-[36px] sm:px-5 sm:py-5 sm:shadow-[0_35px_120px_rgba(15,23,42,0.10)]">
          <div className="overflow-hidden rounded-[26px] border border-[#f6e6b6] bg-[linear-gradient(180deg,#fffdf7_0%,#fff9ed_100%)] shadow-[0_20px_60px_rgba(194,151,69,0.14)] sm:rounded-[34px]">
            <div className="relative px-5 pb-5 pt-5 sm:px-7 sm:pb-7 sm:pt-7 lg:px-10 lg:pb-8 lg:pt-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_14%_18%,rgba(252,229,159,0.42),rgba(255,255,255,0)_26%),radial-gradient(circle_at_76%_22%,rgba(255,209,74,0.18),rgba(255,255,255,0)_20%),linear-gradient(90deg,rgba(255,250,238,0.98)_0%,rgba(255,248,233,0.92)_52%,rgba(255,248,235,0.62)_72%,rgba(255,248,235,0.32)_100%)]" />
              <div className="absolute right-[22%] top-[12%] hidden h-[140px] w-[140px] rounded-full border border-[#f6e1a9] bg-[rgba(255,233,164,0.18)] lg:block" />
              <div className="absolute right-20 top-12 hidden grid-cols-6 gap-2 opacity-45 lg:grid">
                {Array.from({ length: 24 }).map((_, index) => (
                  <span key={index} className="h-[3px] w-[3px] rounded-full bg-[#ebc96c]" />
                ))}
              </div>

              <div className="relative flex flex-col gap-8 lg:flex-row lg:items-start lg:justify-between">
                <div className="max-w-[520px] pt-2">
                  <span className="inline-flex rounded-full bg-[#eef9ef] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.22em] text-[#157c35] sm:text-xs">
                    Instaport Wallet
                  </span>
                  <h1 className="mt-4 text-5xl font-black tracking-[-0.05em] text-[#0f1830] sm:text-6xl lg:text-[78px] lg:leading-[0.95]">
                    {formatCurrency(walletBalance)}
                  </h1>
                  <p className="mt-5 max-w-[420px] text-base leading-8 text-[#546176] sm:text-[18px]">
                    Your live wallet balance for orders, refunds, instant top-ups, and reward points.
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <div className="inline-flex items-center gap-3 rounded-[18px] border border-[#f2e2b6] bg-white/92 px-4 py-3 shadow-[0_10px_24px_rgba(196,151,69,0.10)]">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#eaf8ee] text-[#138536]">
                        <UserRound size={20} />
                      </span>
                      <span className="text-[18px] font-semibold text-[#182033]">
                        {userProfile?.fullname || "Instaport User"}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-3 rounded-[18px] border border-[#f2e2b6] bg-white/92 px-4 py-3 shadow-[0_10px_24px_rgba(196,151,69,0.10)]">
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#eaf8ee] text-[#138536]">
                        <Phone size={20} />
                      </span>
                      <span className="text-[18px] font-semibold text-[#182033]">
                        {userProfile?.mobileno || "No phone number"}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="relative w-full lg:max-w-[520px]">
                  <div className="flex justify-start gap-3 lg:justify-end">
                    <button
                      type="button"
                      onClick={() => fetchWalletData({ silent: true, force: true })}
                      className="inline-flex items-center justify-center gap-2 rounded-full border border-[#3f4c36] bg-white px-5 py-3 text-sm font-semibold text-[#111827] shadow-[0_10px_24px_rgba(30,41,59,0.10)] transition hover:bg-[#fcfcfc]"
                    >
                      <RefreshCw
                        size={18}
                        className={refreshing ? "animate-spin text-[#16803c]" : "text-[#16803c]"}
                      />
                      Refresh
                    </button>
                    <button
                      type="button"
                      onClick={openTopUpSection}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-[linear-gradient(180deg,#1f9a49_0%,#147735_100%)] px-5 py-3 text-sm font-semibold text-white shadow-[0_16px_28px_rgba(20,119,53,0.24)] transition hover:brightness-105"
                    >
                      <Wallet2 size={18} />
                      Top Up Wallet
                    </button>
                  </div>

                  <div className="relative mt-4 min-h-[320px] overflow-hidden rounded-[28px] lg:min-h-[390px]">
                    <div className="absolute inset-y-2 right-0 w-[88%] rounded-[36px] bg-[radial-gradient(circle_at_58%_46%,rgba(255,214,103,0.9)_0%,rgba(255,222,132,0.62)_28%,rgba(255,244,221,0.12)_60%,rgba(255,248,235,0)_74%)]" />
                    <div className="absolute bottom-12 left-3 hidden h-3 w-3 rounded-full bg-[#14813b] lg:block" />
                    <div className="absolute left-6 top-16 hidden h-3 w-3 rounded-full bg-[#f2b312] lg:block" />
                    <div className="absolute right-10 top-16 hidden h-3 w-3 rounded-full bg-[#f2b312] lg:block" />
                    <div className="absolute right-4 top-28 hidden h-9 w-9 rotate-[18deg] rounded-full border border-[#f6cc6e] bg-[linear-gradient(180deg,#ffd76f_0%,#f0b119_100%)] shadow-[0_10px_18px_rgba(237,176,41,0.25)] lg:flex lg:items-center lg:justify-center">
                      <CreditCard size={16} className="text-white" />
                    </div>
                    <div className="absolute left-0 top-0 h-full w-full bg-[linear-gradient(90deg,#fff9ed_0%,rgba(255,249,237,0.78)_16%,rgba(255,249,237,0.20)_36%,rgba(255,249,237,0)_52%)]" />
                    <div
                      className="absolute bottom-4 right-0 h-[84%] w-[90%] bg-contain bg-right-bottom bg-no-repeat"
                      style={{
                        backgroundImage: "url('/assets/delivery_man_images/topup_img.png')",
                        filter: "drop-shadow(0 24px 50px rgba(233, 183, 66, 0.08))",
                        WebkitMaskImage:
                          "radial-gradient(circle at 62% 58%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.98) 34%, rgba(0,0,0,0.9) 48%, rgba(0,0,0,0.72) 60%, rgba(0,0,0,0.38) 73%, rgba(0,0,0,0.14) 84%, rgba(0,0,0,0) 100%), linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.18) 14%, rgba(0,0,0,0.75) 28%, rgba(0,0,0,1) 40%, rgba(0,0,0,1) 88%, rgba(0,0,0,0.82) 94%, transparent 100%)",
                        maskImage:
                          "radial-gradient(circle at 62% 58%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.98) 34%, rgba(0,0,0,0.9) 48%, rgba(0,0,0,0.72) 60%, rgba(0,0,0,0.38) 73%, rgba(0,0,0,0.14) 84%, rgba(0,0,0,0) 100%), linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.18) 14%, rgba(0,0,0,0.75) 28%, rgba(0,0,0,1) 40%, rgba(0,0,0,1) 88%, rgba(0,0,0,0.82) 94%, transparent 100%)",
                        WebkitMaskComposite: "source-in",
                        maskComposite: "intersect",
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="relative mt-2 grid gap-4 md:grid-cols-3">
                {heroStats.map(({ key, label, value, description, icon: Icon, iconWrapClass }) => (
                  <div
                    key={key}
                    className="rounded-[24px] border border-[#ece8dd] bg-white/96 px-5 py-5 shadow-[0_16px_38px_rgba(30,41,59,0.08)]"
                  >
                    <div className="flex items-center gap-4">
                      <span className={`flex h-16 w-16 items-center justify-center rounded-full ${iconWrapClass}`}>
                        <Icon size={28} />
                      </span>
                      <div>
                        <p className="text-[12px] font-bold uppercase tracking-[0.18em] text-[#7b8596]">
                          {label}
                        </p>
                        <p className="mt-2 text-[24px] font-black text-[#0f1830]">{value}</p>
                        <p className="mt-1 text-[15px] text-[#697588]">{description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex gap-2 sm:mt-8">
            {[
              { key: "transactions", label: "Transactions", icon: ReceiptText },
              { key: "topup", label: "Top Up", icon: Wallet2 },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  className={`inline-flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold transition sm:px-7 ${
                    activeTab === tab.key
                      ? "border-[#1b8a40] bg-[linear-gradient(180deg,#1f9a49_0%,#147735_100%)] text-white shadow-[0_16px_28px_rgba(20,119,53,0.24)]"
                      : "border-[#edf0ea] bg-white text-[#1f2432] shadow-[0_12px_28px_rgba(30,41,59,0.06)] hover:border-[#dfe7db]"
                  }`}
                >
                  <Icon size={18} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center sm:min-h-[420px]">
              <div className="flex flex-col items-center gap-4">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-amber-200 border-t-orange-500 sm:h-16 sm:w-16" />
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 sm:text-sm sm:tracking-[0.24em]">
                  Loading wallet data
                </p>
              </div>
            </div>
          ) : activeTab === "transactions" ? (
            <div className="mt-5 space-y-4 sm:mt-8 sm:space-y-5">
              {transactions.length > 0 ? (
                transactions.map((transaction) => (
                  <HistoryCard
                    key={transaction.id}
                    record={transaction}
                    orderSummary={orderLookup[transaction.orderId]}
                    onOpen={setSelectedRecord}
                  />
                ))
              ) : (
                <div className="relative min-h-[310px] overflow-hidden rounded-[28px] border border-[#eef1eb] bg-white px-6 py-10 text-center shadow-[0_18px_45px_rgba(15,23,42,0.08)] sm:min-h-[360px] sm:rounded-[34px] sm:px-10 sm:py-14">
                  <div className="relative flex flex-col items-center">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#eaf8ee] text-[#16803c] sm:h-24 sm:w-24">
                      <ReceiptText size={34} />
                    </div>
                    <h3 className="mt-6 text-[26px] font-bold tracking-[-0.03em] text-[#111827]">
                      No transactions yet
                    </h3>
                    <p className="mt-4 max-w-[620px] text-[18px] leading-8 text-[#667085]">
                      Your wallet payments, refunds, cancellations, and reward activity will appear here once you start using the wallet.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              ref={topUpSectionRef}
              className="mt-5 grid gap-4 sm:mt-8 sm:gap-6 xl:grid-cols-[0.95fr_1.05fr]"
            >
              <div className="rounded-[28px] border border-[#eef0ea] bg-white px-6 py-6 shadow-[0_20px_52px_rgba(15,23,42,0.08)] sm:rounded-[34px] sm:px-7 sm:py-7">
                <span className="inline-flex rounded-full bg-[#fff4d9] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#d97706] sm:text-xs">
                  Add Money
                </span>
                <h2 className="mt-4 text-[32px] font-black tracking-[-0.04em] text-[#0f172a] sm:text-[44px]">
                  Top up your wallet instantly
                </h2>
                <p className="mt-3 max-w-[470px] text-[18px] leading-8 text-[#667085]">
                  Use Razorpay to add funds, then use that wallet balance for orders on the website.
                </p>

                <div className="mt-8 rounded-[24px] border border-[#f5e8c4] bg-[linear-gradient(180deg,#fff9ea_0%,#fff4de_100%)] p-5">
                  <label
                    htmlFor="wallet-topup-amount"
                    className="text-[12px] font-bold uppercase tracking-[0.18em] text-[#667085]"
                  >
                    Amount
                  </label>
                  <div className="mt-4 flex items-center rounded-[18px] border border-[#ede6d4] bg-white px-4 py-4 shadow-[0_10px_24px_rgba(30,41,59,0.05)]">
                    <span className="text-[30px] font-black text-slate-900">{"\u20B9"}</span>
                    <input
                      id="wallet-topup-amount"
                      type="number"
                      min="10"
                      step="1"
                      value={topUpAmount}
                      onChange={(event) => setTopUpAmount(event.target.value)}
                      className="w-full bg-transparent px-3 text-[34px] font-black text-slate-950 outline-none placeholder:text-slate-300"
                      placeholder="500"
                    />
                  </div>
                  <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#98a2b3]">
                    Minimum {"\u20B9"}10. Whole numbers only.
                  </p>

                  <div className="mt-5 flex flex-wrap gap-3">
                    {QUICK_TOPUP_AMOUNTS.map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => setTopUpAmount(String(amount))}
                        className="rounded-full border border-[#efc66f] bg-white px-4 py-2 text-sm font-bold text-[#c46c00] transition hover:bg-[#fff8eb]"
                      >
                        {"\u20B9"}{amount}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleTopUp}
                  disabled={processingTopUp}
                  className="mt-6 inline-flex h-[62px] w-full items-center justify-center gap-3 rounded-[18px] bg-[linear-gradient(180deg,#1f9a49_0%,#147735_100%)] px-6 text-[22px] font-semibold text-white shadow-[0_18px_30px_rgba(20,119,53,0.24)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:brightness-90"
                >
                  <CreditCard size={20} />
                  {processingTopUp ? "Starting payment..." : "Add Money Now"}
                </button>
              </div>

              <div className="rounded-[28px] border border-[#dcefe3] bg-[linear-gradient(180deg,#fcfffd_0%,#f7fdf9_100%)] p-6 shadow-[0_20px_52px_rgba(15,23,42,0.08)] sm:rounded-[34px] sm:p-7">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <span className="inline-flex rounded-full bg-[#eef9ef] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#16803c] sm:text-xs">
                      Top-up History
                    </span>
                    <h2 className="mt-4 text-[32px] font-black tracking-[-0.04em] text-[#0f172a] sm:text-[44px]">
                      Every wallet recharge
                    </h2>
                  </div>
                  <div className="rounded-full bg-[#eef9ef] px-4 py-2 text-sm font-bold text-[#16803c]">
                    {topups.length} records
                  </div>
                </div>

                <div className="mt-6 space-y-4 sm:space-y-5">
                  {topups.length > 0 ? (
                    topups.map((topup) => (
                      <HistoryCard
                        key={topup.id}
                        record={topup}
                        orderSummary={null}
                        onOpen={setSelectedRecord}
                      />
                    ))
                  ) : (
                    <div className="flex min-h-[320px] flex-col items-center justify-center rounded-[26px] border border-dashed border-[#d7eadc] bg-white/82 p-8 text-center shadow-[inset_0_0_0_1px_rgba(220,239,227,0.5)] sm:min-h-[360px] sm:rounded-[30px]">
                      <div className="flex h-20 w-20 items-center justify-center rounded-full bg-white text-emerald-600 shadow-[0_14px_30px_rgba(15,23,42,0.10)]">
                        <Wallet2 size={34} />
                      </div>
                      <h3 className="mt-6 text-[28px] font-bold tracking-[-0.03em] text-[#111827]">
                        No top-ups yet
                      </h3>
                      <p className="mt-4 max-w-md text-[18px] leading-8 text-[#667085]">
                        Add money once and your successful wallet recharges will be listed here with downloadable bills.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />

      <TransactionDetailModal
        record={selectedRecord}
        orderSummary={selectedOrderSummary}
        userProfile={userProfile}
        onClose={() => setSelectedRecord(null)}
        onDownload={handleDownloadReceipt}
      />
    </section>
  );
};

export default Wallet;
